import {memo} from 'react';

import styled, {css, keyframes} from 'styled-components';

interface CircleTimerProps {
  timeoutSeconds: number;
  size: number;
  strokeWidth?: number;
  /** When true the circle starts full and empties out, instead of starting empty and filling up */
  reversed?: boolean;
  color?: string;
}

const DEFAULT_STROKE_WIDTH = 4;

const buildAnimation = (circumference: number, duration: number, reversed: boolean) => {
  const fill = keyframes`
    from {
      stroke-dashoffset: ${reversed ? 0 : circumference};
    }

    to {
      stroke-dashoffset: ${reversed ? circumference : 0};
    }
  `;

  return css`
    animation: ${fill} ${duration}s linear forwards;
  `;
};

const ProgressCircle = styled.circle<{
  $circumference: number;
  $duration: number;
  $strokeWidth: number;
  $reversed: boolean;
  $color?: string;
}>`
  fill: none;
  stroke: ${({theme, $color}) => $color || theme.colors.contentBrand};
  stroke-width: ${({$strokeWidth}) => $strokeWidth};
  stroke-dasharray: ${({$circumference}) => $circumference};
  stroke-dashoffset: ${({$circumference, $reversed}) => ($reversed ? 0 : $circumference)};
  stroke-linecap: round;
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
  ${({$circumference, $duration, $reversed}) => buildAnimation($circumference, $duration, $reversed)}
`;

const CircleTimer = memo(
  ({timeoutSeconds, size, strokeWidth = DEFAULT_STROKE_WIDTH, reversed = false, color}: CircleTimerProps) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const center = size / 2;

    return (
      <svg width={size} height={size}>
        <ProgressCircle
          cx={center}
          cy={center}
          r={radius}
          $circumference={circumference}
          $duration={timeoutSeconds}
          $strokeWidth={strokeWidth}
          $reversed={reversed}
          $color={color}
        />
      </svg>
    );
  },
);

CircleTimer.displayName = 'CircleTimer';

export default CircleTimer;
